import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, XCircle, CalendarClock } from 'lucide-react';
import { 
  getLocalScheduledNotifications, 
  cancelLocalScheduledNotification 
} from '@/services/notifications/scheduleOperations';
import { useToast } from '@/hooks/use-toast';
import { ScheduledNotification } from '@/services/notifications/types';

interface ScheduledNotificationsProps {
  procurementId?: string;
}

export function ScheduledNotifications({ procurementId }: ScheduledNotificationsProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [scheduledNotifications, setScheduledNotifications] = useState<ScheduledNotification[]>([]);
  const [cancelingId, setCancelingId] = useState<string | null>(null);

  // Load scheduled notifications
  const loadNotifications = async () => {
    setIsLoading(true);
    try {
      const notifications = getLocalScheduledNotifications(procurementId);
      setScheduledNotifications(notifications);
    } catch (error) {
      console.error('Error loading scheduled notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadNotifications();
  }, [procurementId]);

  // Handle cancel button click
  const handleCancel = async (id: string) => {
    try {
      setCancelingId(id);
      const result = cancelLocalScheduledNotification(id);
      if (result.success) {
        await loadNotifications();
      } else {
        toast({
          title: 'Error',
          description: 'Failed to cancel scheduled notification',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error canceling notification:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel scheduled notification',
        variant: 'destructive'
      });
    } finally {
      setCancelingId(null);
    }
  };
  
  // Helper function to format date
  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };
  
  // Helper function to render status badge
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500">Scheduled</Badge>;
      case 'processing':
        return <Badge className="bg-blue-500">Processing</Badge>;
      case 'sent':
        return <Badge className="bg-green-500">Sent</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'cancelled':
        return <Badge variant="outline">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          Scheduled Notifications
        </h3>
        <Button variant="outline" size="sm" onClick={loadNotifications}>
          Refresh
        </Button>
      </div>
      
      {scheduledNotifications.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">
          No scheduled notifications found.
        </p>
      ) : (
        <Table>
          <TableCaption>List of scheduled notifications</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Message Preview</TableHead>
              <TableHead>Scheduled For</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {scheduledNotifications.map((notification) => (
              <TableRow key={notification.id}>
                <TableCell className="font-mono text-xs max-w-[300px] truncate">
                  {notification.message}
                </TableCell>
                <TableCell>{formatDateTime(notification.scheduled_time)}</TableCell>
                <TableCell>{renderStatusBadge(notification.status)}</TableCell>
                <TableCell className="text-right">
                  {notification.status === 'pending' && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleCancel(notification.id)}
                      disabled={cancelingId === notification.id}
                    >
                      {cancelingId === notification.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 mr-1" />
                          Cancel
                        </>
                      )}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
